import React, { useState } from "react";
import { Bot, Loader2, Sparkles, AlertTriangle, CheckCircle2, Target, Lightbulb, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";
import { tierConfig } from "./AIScoreBadge";

/**
 * Carte du Profil IA — résumé GPT/Gemini, score, points forts, risques, actions.
 *
 * Props :
 *   evaluation     : objet ai_evaluation (peut être null si jamais évalué)
 *   onEvaluate     : async () => void  (déclenche le scoring)
 *   loading        : bool
 */
const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso.slice(0, 16);
  }
};

const Bullet = ({ icon: Icon, color, text }) => (
  <li className="flex items-start gap-2 text-sm text-slate-300">
    <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${color}`} />
    <span className="leading-snug">{text}</span>
  </li>
);

export const AIProfileCard = ({
  evaluation,
  onEvaluate,
  loading = false,
  testId = "ai-profile-card",
  compact = false,
}) => {
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    if (running || loading) return;
    setRunning(true);
    try {
      await onEvaluate?.();
    } finally {
      setRunning(false);
    }
  };

  const isRunning = running || loading;

  // No evaluation yet
  if (!evaluation) {
    return (
      <div
        className="rounded-2xl border border-dashed border-white/10 bg-[#14101c]/60 p-5 md:p-6 text-center"
        data-testid={`${testId}-empty`}
      >
        <Bot className="w-8 h-8 text-[#f7c935] mx-auto mb-3" />
        <h3 className="text-sm font-bold text-white mb-1">Profil IA non généré</h3>
        <p className="text-xs text-slate-400 mb-4">
          Lancez une évaluation IA pour obtenir un score, un résumé et des actions concrètes.
        </p>
        <Button
          onClick={handleRun}
          disabled={isRunning}
          className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b800] font-bold"
          data-testid="ai-profile-evaluate-btn"
        >
          {isRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Évaluer avec l'IA
        </Button>
      </div>
    );
  }

  const cfg = tierConfig(evaluation.tier);
  const score = evaluation.score || 0;
  const isFallback = evaluation.source === "fallback" || evaluation.model === "heuristic";

  return (
    <div
      className={`rounded-2xl border ${cfg.border} ${cfg.bg} p-5 md:p-6 relative overflow-hidden`}
      data-testid={testId}
    >
      {/* Background gradient accent */}
      <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gradient-to-br ${cfg.gradient} opacity-10 blur-2xl pointer-events-none`} />

      <div className="flex items-start justify-between gap-3 flex-wrap relative">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${cfg.gradient} flex items-center justify-center text-2xl shadow-lg`}>
            {cfg.icon}
          </div>
          <div>
            <div className={`text-2xl md:text-3xl font-black ${cfg.text}`} data-testid="ai-score-value">
              {score}
              <span className="text-base text-slate-400 font-medium"> /100</span>
            </div>
            <div className={`text-xs font-bold ${cfg.text} tracking-wider`}>
              PROFIL {cfg.label}
              {evaluation.confidence !== undefined && (
                <span className="text-slate-500 font-normal"> · confiance {Math.round(evaluation.confidence * 100)}%</span>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={handleRun}
          disabled={isRunning}
          variant="outline"
          size="sm"
          className="border-white/15 text-slate-300 hover:bg-white/5 text-xs"
          data-testid="ai-profile-reevaluate-btn"
        >
          {isRunning ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
          Réévaluer
        </Button>
      </div>

      {/* Headline */}
      {evaluation.headline && (
        <div className="mt-4 text-base font-semibold text-white leading-snug" data-testid="ai-profile-headline">
          {evaluation.headline}
        </div>
      )}

      {/* Summary */}
      {evaluation.profile_summary && (
        <p className="mt-2 text-sm text-slate-300 leading-relaxed" data-testid="ai-profile-summary">
          {evaluation.profile_summary}
        </p>
      )}

      {!compact && (
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          {/* Strengths */}
          {(evaluation.strengths || []).length > 0 && (
            <div className="bg-black/20 dark:bg-black/20 rounded-xl p-3 border border-white/5" data-testid="ai-strengths">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Points forts
              </div>
              <ul className="space-y-1.5">
                {evaluation.strengths.map((s, i) => (
                  <Bullet key={i} icon={CheckCircle2} color="text-emerald-400" text={s} />
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {(evaluation.risks || []).length > 0 && (
            <div className="bg-black/20 dark:bg-black/20 rounded-xl p-3 border border-white/5" data-testid="ai-risks">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-400 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Risques
              </div>
              <ul className="space-y-1.5">
                {evaluation.risks.map((r, i) => (
                  <Bullet key={i} icon={AlertTriangle} color="text-rose-400" text={r} />
                ))}
              </ul>
            </div>
          )}

          {/* Next actions */}
          {(evaluation.next_actions || []).length > 0 && (
            <div className="bg-black/20 dark:bg-black/20 rounded-xl p-3 border border-white/5" data-testid="ai-next-actions">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-400 mb-2">
                <Target className="w-3.5 h-3.5" /> Prochaines actions
              </div>
              <ul className="space-y-1.5">
                {evaluation.next_actions.map((a, i) => (
                  <Bullet key={i} icon={Target} color="text-amber-400" text={a} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Intent signals */}
      {!compact && (evaluation.intent_signals || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5" data-testid="ai-intent-signals">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-400 mb-2">
            <TrendingUp className="w-3.5 h-3.5" /> Signaux d'achat détectés
          </div>
          <div className="flex flex-wrap gap-1.5">
            {evaluation.intent_signals.map((sig, i) => (
              <span
                key={i}
                className="text-xs px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/20 inline-flex items-center gap-1.5"
              >
                <Lightbulb className="w-3 h-3" /> {sig}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recommended status */}
      {evaluation.recommended_status && (
        <div className="mt-3 text-xs text-slate-400">
          <span className="text-slate-500">Statut recommandé :</span>{" "}
          <span className="font-medium text-slate-200">{evaluation.recommended_status}</span>
        </div>
      )}

      {/* Footer meta */}
      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] text-slate-500">
        <span>
          {isFallback ? "⚙️ Évaluation heuristique (IA indisponible)" : `🤖 ${evaluation.model || "Gemini"}`}
        </span>
        {evaluation.evaluated_at && <span>évalué le {formatDate(evaluation.evaluated_at)}</span>}
      </div>
    </div>
  );
};

export default AIProfileCard;
